// src/pages/DashboardCommercialCRMPage.jsx
// C3.2.6 — Dashboard Comercial ORBIS estilo Lead Web ORBIS
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";


const PRODUCT_LINES = [
    "",
    "ORBIS PMO",
    "Gestión institucional",
    "CRM institucional",
    "Proyectos y compromisos",
    "Eventos institucionales",
    "Directorio estratégico",
    "Otro"
];

function addDaysYmd(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

async function safeJson(url, options = {}) {
    const res = await fetch(url, options);
    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        throw new Error(`Respuesta no JSON (${res.status}): ${text.slice(0, 220)}`);
    }
    if (!res.ok || data?.success === false || data?.ok === false) {
        throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
    }
    return data;
}

function asArray(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
}

function n(value) {
    const x = Number(value || 0);
    return Number.isFinite(x) ? x : 0;
}

function pct(value) {
    return `${Math.round(n(value))}%`;
}

function formatCurrency(value, currency = "MXN") {
    return `${currency} ${n(value).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
}

function formatDateTime(value) {
    if (!value || String(value).startsWith("0000-00-00")) return "—";
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "—";

    const day = String(d.getDate()).padStart(2, "0");
    const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    const month = months[d.getMonth()] || "—";
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "p. m." : "a. m.";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} · ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
}

function Badge({ children, tone = "gray" }) {
    const tones = {
        gray: "border-[#24537a] bg-[#0f2d48] text-[#d9e8f5]",
        blue: "border-[#0ea5e9]/50 bg-[#0b3552] text-[#7dd3fc]",
        green: "border-[#10b981]/40 bg-[#0b3a36] text-[#7cebd0]",
        amber: "border-[#d4af37]/45 bg-[#3a3110] text-[#f6d76b]",
        red: "border-[#ef4444]/40 bg-[#38161c] text-[#fca5a5]",
        orange: "border-[#f97316]/40 bg-[#3b2413] text-[#fdba74]",
        purple: "border-[#8b5cf6]/40 bg-[#24163d] text-[#c4b5fd]",
    };
    return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wide ${tones[tone] || tones.gray}`}>{children}</span>;
}

function KpiCard({ title, value, subtitle, tone = "default", onClick, accent }) {
    const tones = {
        default: "border-[#0b4d78] bg-[#08233D]",
        blue: "border-[#0ea5e9]/45 bg-[#0b2740]",
        red: "border-[#ef4444]/40 bg-[#2b1117]",
        amber: "border-[#d4af37]/45 bg-[#3a3110]",
        green: "border-[#10b981]/40 bg-[#0a332e]",
        purple: "border-[#8b5cf6]/40 bg-[#24163d]",
        slate: "border-[#24537a] bg-[#08233D]",
    };
    return (
        <button type="button" onClick={onClick} className={`group text-left rounded-[22px] border p-4 shadow-[0_12px_30px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:border-[#d4af37] ${tones[tone] || tones.default}`}>
            <div className="flex justify-between gap-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8fb9da]">{title}</div>
                {accent ? <div className="text-[10px] text-[#9fc4df] group-hover:text-[#d4af37]">{accent}</div> : null}
            </div>
            <div className="text-3xl font-black text-white mt-2">{value}</div>
            {subtitle ? <div className="text-[10px] text-[#9fc4df] mt-1">{subtitle}</div> : null}
        </button>
    );
}

function Section({ title, subtitle, right, children }) {
    return (
        <div className="dashboard-card rounded-[24px] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#0b4d78] flex items-start justify-between gap-3 bg-[#031425]">
                <div>
                    <h3 className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#d4af37]">{title}</h3>
                    {subtitle ? <p className="text-[11px] text-[#9fc4df] mt-1">{subtitle}</p> : null}
                </div>
                {right ? <div>{right}</div> : null}
            </div>
            {children}
        </div>
    );
}

function BarRow({ label, value, max, right, color = "#0ea5e9", sub }) {
    const width = max > 0 ? Math.max(4, Math.round((n(value) / max) * 100)) : 0;
    return (
        <div className="space-y-1 rounded-2xl border border-[#0b4d78] bg-[#08233d] p-3">
            <div className="flex justify-between gap-3 text-[11px]">
                <span className="font-semibold text-white truncate">{label || "Sin dato"}</span>
                <span className="text-[#9fc4df] whitespace-nowrap">{right ?? value}</span>
            </div>
            {sub ? <div className="text-[10px] text-[#8fb9da] truncate">{sub}</div> : null}
            <div className="h-2.5 rounded-full bg-[#0b2740] overflow-hidden border border-[#0b4d78]">
                <div className="h-full rounded-full" style={{ width: `${width}%`, background: color }} />
            </div>
        </div>
    );
}

function stageTone(stageType) {
    if (stageType === "ganada") return "green";
    if (stageType === "perdida") return "red";
    return "blue";
}

function urgencyTone(urgency) {
    if (urgency === "critica") return "red";
    if (urgency === "alta") return "orange";
    if (urgency === "media") return "amber";
    if (urgency === "baja") return "green";
    return "gray";
}

function DashboardStyle() {
    return (
        <style>{`
      .crm-pro{color:#f5fbff}
      .crm-pro select option{background:#061a2f;color:white}
      .crm-pro .dashboard-hero{background:linear-gradient(135deg,#03111f 0%,#031a2f 55%,#04223d 100%);border:1px solid rgba(11,93,140,.8);box-shadow:0 20px 45px rgba(0,0,0,.28)}
      .crm-pro .dashboard-card{border:1px solid rgba(11,93,140,.82);background:#04192b;box-shadow:0 12px 30px rgba(0,0,0,.24)}
      .crm-pro .dashboard-actions-bar{background:#031425;border:1px solid #0b4d78;border-radius:22px}
      .crm-pro .dashboard-btn{border:1px solid #24537a;background:#0b2740;color:#f5fbff;border-radius:999px;padding:10px 14px;font-size:12px;font-weight:700}
      .crm-pro .dashboard-btn:hover{background:#10324f;border-color:#d4af37}
      .crm-pro .dashboard-btn-primary{background:#d4af37;color:#081a2b;border-color:#d4af37}
      .crm-pro .dashboard-btn-primary:hover{background:#e8c457}
      .crm-pro table{border-collapse:separate;border-spacing:0;background:#061a2f}
      .crm-pro table tbody tr{transition:background .18s ease, box-shadow .18s ease;background:#08233d!important}
      .crm-pro table tbody tr:nth-child(even){background:#092a49!important}
      .crm-pro table tbody tr:hover{background:rgba(56,189,248,.075)!important;box-shadow:inset 3px 0 0 #d4af37}
      .crm-pro table tbody td{border-top:1px solid #24587c;background:transparent!important;color:#f5fbff}
      .crm-pro table thead th{position:sticky;top:0;z-index:10;background:#061a2f!important;color:#8fb9da;border-bottom:1px solid #24587c;text-transform:uppercase;letter-spacing:.13em}
      .crm-pro .input-pro{width:100%;border:1px solid #0b5f91;border-radius:.85rem;padding:.65rem .75rem;font-size:12px;outline:none;background:#0b2740;color:#fff}
      .crm-pro .input-pro::placeholder{color:#7ea8c7}
      .crm-pro .input-pro:focus{border-color:#d4af37;box-shadow:0 0 0 2px rgba(212,175,55,.18)}
      .crm-pro .label-pro{display:block;font-size:11px;color:#8fb9da;margin-bottom:6px;font-weight:700;text-transform:uppercase;letter-spacing:.14em}
      .crm-pro .dashboard-table-scroll{overflow:auto;max-height:62vh}
      .crm-pro .dashboard-table-scroll::-webkit-scrollbar{height:10px;width:10px}
      .crm-pro .dashboard-table-scroll::-webkit-scrollbar-track{background:#061a2f;border-radius:999px}
      .crm-pro .dashboard-table-scroll::-webkit-scrollbar-thumb{background:#2b638e;border-radius:999px}
      .crm-pro .dashboard-table-scroll::-webkit-scrollbar-thumb:hover{background:#d4af37}
    `}</style>
    );
}

function executiveStatus(summary) {
    const total = n(summary.total_opportunities);
    const noNext = n(summary.without_next_action);
    const overdue = n(summary.overdue_activities);
    const next7 = n(summary.next_7_days);
    const noNextRate = total > 0 ? noNext / total : 0;

    if (overdue > 0 || noNextRate >= 0.5) {
        return {
            tone: "red",
            title: "Atención comercial requerida",
            text: `${overdue} actividad(es) vencida(s) y ${noNext} oportunidad(es) sin próxima acción. El foco inmediato es ordenar seguimiento y mover oportunidades fuera de Lead nuevo.`,
        };
    }

    if (next7 > 0 || n(summary.open_opportunities) > 0) {
        return {
            tone: "amber",
            title: "Pipeline activo por impulsar",
            text: `${summary.open_opportunities || 0} oportunidad(es) abiertas y ${next7} actividad(es) próximas. Conviene priorizar demos, propuestas y cierres piloto.`,
        };
    }

    return {
        tone: "green",
        title: "Operación comercial controlada",
        text: "No se detectan alertas críticas en el periodo filtrado. Mantener disciplina de seguimiento y actualización de oportunidades.",
    };
}

function ActionButton({ children, onClick, tone = "default" }) {
    const tones = {
        default: "border-[#24537a] bg-[#0b2740] text-[#f5fbff] hover:bg-[#10324f]",
        blue: "border-[#0ea5e9]/45 bg-[#0b3552] text-[#7dd3fc] hover:bg-[#10324f]",
        green: "border-[#10b981]/40 bg-[#0b3a36] text-[#7cebd0] hover:bg-[#10324f]",
        amber: "border-[#d4af37]/45 bg-[#3a3110] text-[#f6d76b] hover:bg-[#10324f]",
        red: "border-[#ef4444]/40 bg-[#38161c] text-[#fca5a5] hover:bg-[#10324f]",
    };

    return (
        <button type="button" onClick={onClick} className={`px-2.5 py-1.5 rounded-xl border text-[11px] font-semibold ${tones[tone] || tones.default}`}>
            {children}
        </button>
    );
}

export default function DashboardCommercialCRMPage() {
    const navigate = useNavigate();

    const [data, setData] = useState({
        summary: {},
        by_stage: [],
        by_campaign: [],
        by_product: [],
        by_pipeline: [],
        activities_by_status: [],
        activities_by_type: [],
        hot_opportunities: [],
        without_next_action: [],
        without_value: [],
        recent_activities: [],
    });

    const [campaigns, setCampaigns] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [accounts, setAccounts] = useState([]);

    const [filters, setFilters] = useState({
        date_from: addDaysYmd(-30),
        date_to: addDaysYmd(60),
        product_line: "",
        campaign_id: "",
        pipeline_id: "",
        account_id: "",
    });

    const [sortHot, setSortHot] = useState({ key: "value", direction: "desc" });
    const [sortNoNext, setSortNoNext] = useState({ key: "name", direction: "asc" });
    const [sortActivities, setSortActivities] = useState({ key: "date", direction: "asc" });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    function buildQuery(extra = {}) {
        const merged = { ...filters, ...extra };
        const params = new URLSearchParams();
        Object.entries(merged).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") params.set(key, value);
        });
        return params.toString();
    }

    function opportunityUrl(id) {
        return `/opportunities/${id}/edit`;
    }

    function agendaForOpportunity(row, newActivity = false) {
        const params = new URLSearchParams();
        if (row?.id || row?.opportunity_id) params.set("opportunity_id", row.id || row.opportunity_id);
        if (row?.account_id) params.set("account_id", row.account_id);
        if (newActivity) params.set("new_activity", "1");
        return `/agenda?${params.toString()}`;
    }

    function agendaFiltered(filter = "") {
        if (!filter) return "/agenda";
        return `/agenda?view=${encodeURIComponent(filter)}`;
    }

    async function loadCatalogs() {
        const [campData, pipeData, accData] = await Promise.all([
            safeJson(`${API_BASE}/campaigns_list.php?active_only=1`),
            safeJson(`${API_BASE}/pipelines_list.php?active_only=1`),
            safeJson(`${API_BASE}/accounts_list.php`),
        ]);
        setCampaigns(asArray(campData));
        setPipelines(asArray(pipeData));
        setAccounts(asArray(accData));
    }

    async function loadDashboard(extra = {}) {
        try {
            setLoading(true);
            setError("");
            const query = buildQuery(extra);
            const dashboard = await safeJson(`${API_BASE}/crm_commercial_dashboard.php?${query}`);
            setData({
                summary: dashboard.summary || {},
                by_stage: dashboard.by_stage || [],
                by_campaign: dashboard.by_campaign || [],
                by_product: dashboard.by_product || [],
                by_pipeline: dashboard.by_pipeline || [],
                activities_by_status: dashboard.activities_by_status || [],
                activities_by_type: dashboard.activities_by_type || [],
                hot_opportunities: dashboard.hot_opportunities || [],
                without_next_action: dashboard.without_next_action || [],
                without_value: dashboard.without_value || [],
                recent_activities: dashboard.recent_activities || [],
            });
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudo cargar el dashboard comercial.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        async function boot() {
            await loadCatalogs();
            await loadDashboard();
        }
        boot();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleFilterChange(e) {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    }

    function applyFilters() {
        loadDashboard();
    }

    function clearFilters() {
        const reset = {
            date_from: addDaysYmd(-30),
            date_to: addDaysYmd(60),
            product_line: "",
            campaign_id: "",
            pipeline_id: "",
            account_id: "",
        };
        setFilters(reset);
        loadDashboard(reset);
    }

    function setQuickFocus(type) {
        if (type === "orbis") {
            const reset = { ...filters, product_line: "ORBIS PMO" };
            setFilters(reset);
            loadDashboard(reset);
        }
    }

    function sortRows(rows, config, getter) {
        const direction = config.direction === "asc" ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = getter(a, config.key);
            const bv = getter(b, config.key);
            if (typeof av === "number" || typeof bv === "number") return (n(av) - n(bv)) * direction;
            return String(av || "").localeCompare(String(bv || ""), "es", { numeric: true, sensitivity: "base" }) * direction;
        });
    }

    function requestSort(current, setter, key) {
        setter({ key, direction: current.key === key && current.direction === "asc" ? "desc" : "asc" });
    }

    function SortButton({ current, setter, sortKey, children }) {
        const active = current.key === sortKey;
        return (
            <button type="button" onClick={() => requestSort(current, setter, sortKey)} className={`inline-flex items-center gap-1 hover:text-[#d4af37] ${active ? "text-[#d4af37]" : ""}`} title="Ordenar">
                <span>{children}</span>
                <span className="text-[10px]">{active ? (current.direction === "asc" ? "▲" : "▼") : "↕"}</span>
            </button>
        );
    }

    const summary = data.summary || {};
    const executive = executiveStatus(summary);

    const maxStage = useMemo(() => Math.max(0, ...data.by_stage.map((r) => n(r.opportunities_count))), [data.by_stage]);
    const maxCampaign = useMemo(() => Math.max(0, ...data.by_campaign.map((r) => n(r.opportunities_count))), [data.by_campaign]);
    const maxProduct = useMemo(() => Math.max(0, ...data.by_product.map((r) => n(r.opportunities_count))), [data.by_product]);
    const maxActivityType = useMemo(() => Math.max(0, ...data.activities_by_type.map((r) => n(r.total))), [data.activities_by_type]);

    const hotRows = useMemo(() => sortRows(data.hot_opportunities, sortHot, (r, key) => {
        if (key === "value") return n(r.pilot_value) + n(r.monthly_potential);
        if (key === "probability") return n(r.probability);
        if (key === "urgency") return r.urgency || "";
        if (key === "stage") return r.stage_name || "";
        return r.name || "";
    }), [data.hot_opportunities, sortHot]);

    const noNextRows = useMemo(() => sortRows(data.without_next_action, sortNoNext, (r, key) => {
        if (key === "pipeline") return r.pipeline_name || "";
        if (key === "stage") return r.stage_name || "";
        if (key === "product") return r.product_line || "";
        return r.name || "";
    }), [data.without_next_action, sortNoNext]);

    const activityRows = useMemo(() => sortRows(data.recent_activities, sortActivities, (r, key) => {
        if (key === "date") return r.start_datetime || "";
        if (key === "status") return r.status || "";
        if (key === "account") return `${r.account_name || ""} ${r.opportunity_name || ""}`;
        return r.title || "";
    }), [data.recent_activities, sortActivities]);

    const conversionRate = n(summary.total_opportunities) > 0 ? (n(summary.won_opportunities) / n(summary.total_opportunities)) * 100 : 0;
    const disciplineRate = n(summary.total_opportunities) > 0 ? ((n(summary.total_opportunities) - n(summary.without_next_action)) / n(summary.total_opportunities)) * 100 : 0;
    const valueCaptureRate = n(summary.total_opportunities) > 0 ? ((n(summary.total_opportunities) - n(summary.without_pilot_value)) / n(summary.total_opportunities)) * 100 : 0;

    return (
        <MainLayout>
            <DashboardStyle />
            <div className="crm-pro min-h-screen bg-[#041426] px-3 py-6 md:px-6 md:py-8"><div className="mx-auto max-w-[1500px] space-y-6">
                <div className="dashboard-hero rounded-[28px] px-6 py-6 md:px-7 md:py-7 flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                    <div>
                        <p className="mb-3 inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]"><span className="h-px w-10 bg-[#D4AF37]" />CRM Comercial</p><h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">Dashboard Comercial CRM</h2>
                        <p className="text-sm md:text-base text-[#d7e8f6] max-w-3xl leading-relaxed">Lectura ejecutiva accionable de oportunidades, campañas, agenda comercial, valor piloto y continuidad mensual.</p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                        <button type="button" onClick={() => setQuickFocus("orbis")} className="dashboard-btn">Foco ORBIS PMO</button>
                        <button type="button" onClick={() => navigate("/opportunities")} className="dashboard-btn">Oportunidades</button>
                        <button type="button" onClick={() => navigate("/agenda")} className="dashboard-btn">Agenda</button>
                        <button type="button" onClick={() => loadDashboard()} className="dashboard-btn dashboard-btn-primary">Recargar</button>
                    </div>
                </div>

                {error ? <div className="text-xs rounded-xl px-4 py-3 border bg-[#2b1117] text-[#fca5a5] border-[#ef4444]/40">{error}</div> : null}

                <div className={`rounded-[24px] border shadow-[0_12px_30px_rgba(0,0,0,0.24)] p-5 ${executive.tone === "red" ? "bg-[#2b1117] border-[#ef4444]/40" : executive.tone === "amber" ? "bg-[#3a3110] border-[#d4af37]/45" : "bg-[#0a332e] border-[#10b981]/40"}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div>
                            <div className="text-[11px] text-[#9fc4df]">Lectura ejecutiva</div>
                            <h3 className="text-2xl font-black text-white mt-0.5">{executive.title}</h3>
                            <p className="text-xs text-[#d7e8f6] mt-1 max-w-4xl">{executive.text}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 text-[11px]">
                            <button type="button" onClick={() => navigate("/agenda")} className="dashboard-btn">Atender agenda</button>
                            <button type="button" onClick={() => navigate("/opportunities")} className="dashboard-btn dashboard-btn-primary">Ordenar oportunidades</button>
                        </div>
                    </div>
                </div>

                <div className="dashboard-card rounded-[24px] p-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 text-xs">
                        <div><label className="label-pro">Desde</label><input type="date" name="date_from" value={filters.date_from} onChange={handleFilterChange} className="input-pro" /></div>
                        <div><label className="label-pro">Hasta</label><input type="date" name="date_to" value={filters.date_to} onChange={handleFilterChange} className="input-pro" /></div>
                        <div><label className="label-pro">Producto</label><select name="product_line" value={filters.product_line} onChange={handleFilterChange} className="input-pro">{PRODUCT_LINES.map((p) => <option key={p || "all"} value={p}>{p || "Todos"}</option>)}</select></div>
                        <div><label className="label-pro">Campaña</label><select name="campaign_id" value={filters.campaign_id} onChange={handleFilterChange} className="input-pro"><option value="">Todas</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                        <div><label className="label-pro">Pipeline</label><select name="pipeline_id" value={filters.pipeline_id} onChange={handleFilterChange} className="input-pro"><option value="">Todos</option>{pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                        <div><label className="label-pro">Cuenta</label><select name="account_id" value={filters.account_id} onChange={handleFilterChange} className="input-pro"><option value="">Todas</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
                        <div className="xl:col-span-6 flex justify-end gap-2">
                            <button type="button" onClick={clearFilters} className="dashboard-btn">Limpiar</button>
                            <button type="button" onClick={applyFilters} className="dashboard-btn dashboard-btn-primary">Aplicar filtros</button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                    <KpiCard title="Oportunidades" value={summary.total_opportunities || 0} subtitle="Total CRM" tone="blue" accent="Pipeline" onClick={() => navigate("/opportunities")} />
                    <KpiCard title="Abiertas" value={summary.open_opportunities || 0} subtitle="Pipeline activo" tone="amber" accent={pct(summary.avg_probability)} onClick={() => navigate("/opportunities")} />
                    <KpiCard title="Ganadas" value={summary.won_opportunities || 0} subtitle={`Conversión ${pct(conversionRate)}`} tone="green" onClick={() => navigate("/opportunities")} />
                    <KpiCard title="Sin próxima acción" value={summary.without_next_action || 0} subtitle={`Disciplina ${pct(disciplineRate)}`} tone={n(summary.without_next_action) > 0 ? "red" : "green"} onClick={() => navigate("/opportunities")} />
                    <KpiCard title="Sin valor piloto" value={summary.without_pilot_value || 0} subtitle={`Captura ${pct(valueCaptureRate)}`} tone={n(summary.without_pilot_value) > 0 ? "amber" : "green"} onClick={() => navigate("/opportunities")} />
                    <KpiCard title="Actividades hoy" value={summary.today_activities || 0} subtitle="Agenda del día" tone="blue" onClick={() => navigate("/agenda")} />
                    <KpiCard title="Vencidas" value={summary.overdue_activities || 0} subtitle="Seguimiento retrasado" tone={n(summary.overdue_activities) > 0 ? "red" : "green"} onClick={() => navigate("/agenda")} />
                    <KpiCard title="Demos / reuniones" value={summary.demos_or_meetings || 0} subtitle="Periodo filtrado" tone="purple" onClick={() => navigate("/agenda")} />
                    <KpiCard title="Valor piloto abierto" value={formatCurrency(summary.open_pilot_value || 0)} subtitle="Potencial implementación" tone="amber" />
                    <KpiCard title="Potencial mensual" value={formatCurrency(summary.open_monthly_potential || 0)} subtitle="Continuidad estimada" tone="green" />
                </div>

                {loading ? <div className="text-sm text-[#9fc4df] dashboard-card rounded-[24px] p-5">Cargando dashboard comercial.</div> : null}

                <Section title="Qué atender hoy" subtitle="Acciones ejecutables desde el dashboard.">
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-xs">
                        <button type="button" onClick={() => navigate("/opportunities")} className="text-left rounded-2xl border border-[#ef4444]/40 bg-[#2b1117] p-4 hover:border-[#d4af37]">
                            <div className="font-semibold text-[#fca5a5]">Ordenar próximas acciones</div>
                            <p className="text-[#fca5a5] mt-1">{summary.without_next_action || 0} oportunidad(es) sin seguimiento definido.</p>
                        </button>
                        <button type="button" onClick={() => navigate("/agenda")} className="text-left rounded-2xl border border-[#d4af37]/45 bg-[#3a3110] p-4 hover:border-[#d4af37]">
                            <div className="font-semibold text-[#f6d76b]">Atender agenda vencida</div>
                            <p className="text-[#f6d76b] mt-1">{summary.overdue_activities || 0} actividad(es) retrasada(s).</p>
                        </button>
                        <button type="button" onClick={() => navigate("/agenda")} className="text-left rounded-2xl border border-[#0ea5e9]/45 bg-[#0b2740] p-4 hover:border-[#d4af37]">
                            <div className="font-semibold text-[#7dd3fc]">Convertir demos en propuesta</div>
                            <p className="text-[#7dd3fc] mt-1">{summary.demos_or_meetings || 0} demo(s)/reunión(es) registradas.</p>
                        </button>
                        <button type="button" onClick={() => navigate("/opportunities")} className="text-left rounded-2xl border border-[#10b981]/40 bg-[#0a332e] p-4 hover:border-[#d4af37]">
                            <div className="font-semibold text-[#7cebd0]">Capturar valor comercial</div>
                            <p className="text-[#7cebd0] mt-1">{summary.without_pilot_value || 0} oportunidad(es) sin valor piloto.</p>
                        </button>
                    </div>
                </Section>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <Section title="Embudo comercial" subtitle="Lectura visual por etapa del pipeline.">
                        <div className="p-4 space-y-3">
                            {data.by_stage.length === 0 ? <div className="text-xs text-[#9fc4df]">Sin datos por etapa.</div> : data.by_stage.map((row) => (
                                <BarRow key={row.stage_id || row.stage_name || "sin-etapa"} label={row.stage_name || "Sin etapa"} value={row.opportunities_count} max={maxStage} right={`${row.opportunities_count || 0} · ${formatCurrency(row.pilot_value)}`} sub={`Prob. prom. ${pct(row.avg_probability)} · ${row.stage_type || "abierta"}`} color={row.color_hex || "#2563eb"} />
                            ))}
                        </div>
                    </Section>

                    <Section title="Campañas" subtitle="Generación comercial por campaña.">
                        <div className="p-4 space-y-3">
                            {data.by_campaign.length === 0 ? <div className="text-xs text-[#9fc4df]">Sin datos por campaña.</div> : data.by_campaign.map((row) => (
                                <BarRow key={row.campaign_id || row.campaign_name || "sin-campana"} label={row.campaign_name || "Sin campaña"} value={row.opportunities_count} max={maxCampaign} right={`${row.opportunities_count || 0} opp`} sub={`Abiertas ${row.open_count || 0} · Ganadas ${row.won_count || 0}`} color="#7c3aed" />
                            ))}
                        </div>
                    </Section>

                    <Section title="Actividades por tipo" subtitle="Demos, propuestas, reuniones y seguimientos.">
                        <div className="p-4 space-y-3">
                            {data.activities_by_type.length === 0 ? <div className="text-xs text-[#9fc4df]">Sin actividades en el periodo.</div> : data.activities_by_type.map((row) => (
                                <BarRow key={row.type || "sin-tipo"} label={row.type || "Sin tipo"} value={row.total} max={maxActivityType} right={`${row.total || 0}`} color="#ea580c" />
                            ))}
                        </div>
                    </Section>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Section title="Oportunidades calientes" subtitle="Priorizadas por urgencia, valor y actualización.">
                        <div className="dashboard-table-scroll">
                            <table className="min-w-full text-xs">
                                <thead><tr className="text-left text-[11px] font-semibold"><th className="px-3 py-2"><SortButton current={sortHot} setter={setSortHot} sortKey="name">Oportunidad</SortButton></th><th className="px-3 py-2"><SortButton current={sortHot} setter={setSortHot} sortKey="stage">Etapa</SortButton></th><th className="px-3 py-2"><SortButton current={sortHot} setter={setSortHot} sortKey="urgency">Urgencia</SortButton></th><th className="px-3 py-2 text-right"><SortButton current={sortHot} setter={setSortHot} sortKey="value">Valor</SortButton></th><th className="px-3 py-2 text-right">Acciones</th></tr></thead>
                                <tbody>
                                    {hotRows.length === 0 ? <tr><td colSpan={5} className="px-3 py-5 text-center text-[#9fc4df]">Sin oportunidades.</td></tr> : hotRows.map((opp) => (
                                        <tr key={opp.id} className="">
                                            <td className="px-3 py-2"><button type="button" onClick={() => navigate(opportunityUrl(opp.id))} className="font-semibold text-white hover:text-[#d4af37] text-left">{opp.name}</button><div className="text-[11px] text-[#9fc4df]">{opp.account_name || "Sin cuenta"} · {opp.product_line || "Sin producto"}</div></td>
                                            <td className="px-3 py-2"><Badge tone={stageTone(opp.stage_type)}>{opp.stage_name || "Sin etapa"}</Badge></td>
                                            <td className="px-3 py-2"><Badge tone={urgencyTone(opp.urgency)}>{opp.urgency || "Sin urgencia"}</Badge></td>
                                            <td className="px-3 py-2 text-right"><div>{formatCurrency(opp.pilot_value)}</div><div className="text-[11px] text-[#9fc4df]">Mensual {formatCurrency(opp.monthly_potential)}</div></td>
                                            <td className="px-3 py-2 text-right"><div className="flex flex-wrap justify-end gap-1"><ActionButton onClick={() => navigate(opportunityUrl(opp.id))}>Abrir</ActionButton><ActionButton tone="blue" onClick={() => navigate(agendaForOpportunity(opp, true))}>Actividad</ActionButton></div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>

                    <Section title="Sin próxima acción" subtitle="Oportunidades abiertas que necesitan seguimiento definido.">
                        <div className="dashboard-table-scroll">
                            <table className="min-w-full text-xs">
                                <thead><tr className="text-left text-[11px] font-semibold"><th className="px-3 py-2"><SortButton current={sortNoNext} setter={setSortNoNext} sortKey="name">Oportunidad</SortButton></th><th className="px-3 py-2"><SortButton current={sortNoNext} setter={setSortNoNext} sortKey="pipeline">Pipeline</SortButton></th><th className="px-3 py-2"><SortButton current={sortNoNext} setter={setSortNoNext} sortKey="stage">Etapa</SortButton></th><th className="px-3 py-2 text-right">Acciones</th></tr></thead>
                                <tbody>
                                    {noNextRows.length === 0 ? <tr><td colSpan={4} className="px-3 py-5 text-center text-[#9fc4df]">Todas las oportunidades abiertas tienen próxima acción.</td></tr> : noNextRows.map((opp) => (
                                        <tr key={opp.id} className="">
                                            <td className="px-3 py-2"><div className="font-semibold text-white">{opp.name}</div><div className="text-[11px] text-[#9fc4df]">{opp.account_name || "Sin cuenta"} · {opp.product_line || "Sin producto"}</div></td>
                                            <td className="px-3 py-2">{opp.pipeline_name || "Sin pipeline"}</td>
                                            <td className="px-3 py-2">{opp.stage_name || "Sin etapa"}</td>
                                            <td className="px-3 py-2 text-right"><div className="flex flex-wrap justify-end gap-1"><ActionButton onClick={() => navigate(opportunityUrl(opp.id))}>Abrir</ActionButton><ActionButton tone="blue" onClick={() => navigate(agendaForOpportunity(opp, true))}>Crear actividad</ActionButton></div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                </div>

                {data.without_value.length > 0 ? (
                    <Section title="Sin valor comercial capturado" subtitle="Oportunidades que necesitan valor piloto y potencial mensual.">
                        <div className="dashboard-table-scroll">
                            <table className="min-w-full text-xs">
                                <thead><tr className="text-left text-[11px] font-semibold"><th className="px-3 py-2">Oportunidad</th><th className="px-3 py-2">Cuenta</th><th className="px-3 py-2">Etapa</th><th className="px-3 py-2 text-right">Acción</th></tr></thead>
                                <tbody>
                                    {data.without_value.map((opp) => (
                                        <tr key={opp.id} className="">
                                            <td className="px-3 py-2"><div className="font-semibold text-white">{opp.name}</div><div className="text-[11px] text-[#9fc4df]">{opp.product_line || "Sin producto"}</div></td>
                                            <td className="px-3 py-2">{opp.account_name || "Sin cuenta"}</td>
                                            <td className="px-3 py-2">{opp.stage_name || "Sin etapa"}</td>
                                            <td className="px-3 py-2 text-right"><ActionButton tone="green" onClick={() => navigate(opportunityUrl(opp.id))}>Capturar valor</ActionButton></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                ) : null}

                <Section title="Actividades comerciales recientes" subtitle="Vencidas, próximas y últimos movimientos de la agenda.">
                    <div className="dashboard-table-scroll">
                        <table className="min-w-full text-xs">
                            <thead><tr className="text-left text-[11px] font-semibold"><th className="px-3 py-2"><SortButton current={sortActivities} setter={setSortActivities} sortKey="date">Fecha</SortButton></th><th className="px-3 py-2"><SortButton current={sortActivities} setter={setSortActivities} sortKey="title">Actividad</SortButton></th><th className="px-3 py-2"><SortButton current={sortActivities} setter={setSortActivities} sortKey="account">Cuenta / oportunidad</SortButton></th><th className="px-3 py-2"><SortButton current={sortActivities} setter={setSortActivities} sortKey="status">Estado</SortButton></th><th className="px-3 py-2 text-right">Acciones</th></tr></thead>
                            <tbody>
                                {activityRows.length === 0 ? <tr><td colSpan={5} className="px-3 py-5 text-center text-[#9fc4df]">Sin actividades en el periodo.</td></tr> : activityRows.map((act) => (
                                    <tr key={act.id} className="">
                                        <td className="px-3 py-2">{formatDateTime(act.start_datetime)}</td>
                                        <td className="px-3 py-2"><div className="font-semibold text-white">{act.title}</div><div className="text-[11px] text-[#9fc4df]">{act.type || "Sin tipo"} · {act.channel || "Sin canal"}</div></td>
                                        <td className="px-3 py-2"><div>{act.account_name || "Sin cuenta"}</div><div className="text-[11px] text-[#9fc4df]">{act.opportunity_name || "Sin oportunidad"}</div></td>
                                        <td className="px-3 py-2"><Badge tone={act.status === "realizada" ? "green" : act.status === "cancelada" ? "red" : "blue"}>{act.status || "Sin estado"}</Badge></td>
                                        <td className="px-3 py-2 text-right"><div className="flex flex-wrap justify-end gap-1"><ActionButton onClick={() => navigate(act.opportunity_id ? opportunityUrl(act.opportunity_id) : "/agenda")}>Abrir</ActionButton><ActionButton tone="blue" onClick={() => navigate(act.opportunity_id ? agendaForOpportunity(act, false) : "/agenda")}>Agenda</ActionButton>{act.opportunity_id ? <ActionButton tone="green" onClick={() => navigate(agendaForOpportunity(act, true))}>Siguiente</ActionButton> : null}</div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>
            </div>
            </div>
        </MainLayout>
    );
}
